import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Sortable from "sortablejs";
import { useToast } from "../../../components/Toast";
import { request } from "../../../lib/api";
import type { CourseDetail, ExamAnswer, ExamOut, ExamQuestion, QuestionType } from "../../../lib/types";

export function ExamTab({ course, exam }: { course: CourseDetail; exam: ExamOut | undefined }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(exam?.questions[0]?.id || null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!exam) return;
    if (!exam.questions.find((q) => q.id === selectedId)) {
      setSelectedId(exam.questions[0]?.id || null);
    }
  }, [exam, selectedId]);

  const createExam = useMutation({
    mutationFn: () => request<ExamOut>(`/courses/${course.id}/exam`, {
      method: "POST",
      body: { title: `Examen — ${course.title}` },
    }),
    onSuccess: () => { toast("Examen creat", "success"); qc.invalidateQueries({ queryKey: ["course", course.id, "exam-admin"] }); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  const updateSetting = useMutation({
    mutationFn: (body: object) => request<ExamOut>(`/exams/${exam!.id}`, { method: "PATCH", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["course", course.id, "exam-admin"] }),
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  const addQuestion = useMutation({
    mutationFn: () => request<ExamQuestion>(`/exams/${exam!.id}/questions`, {
      method: "POST",
      body: {
        text: "Întrebare nouă", type: "single", points: 0.5,
        answers: [
          { text: "Răspuns A", is_correct: true },
          { text: "Răspuns B", is_correct: false },
          { text: "Răspuns C", is_correct: false },
          { text: "Răspuns D", is_correct: false },
        ],
      },
    }),
    onSuccess: (q) => { toast("Întrebare adăugată", "success"); setSelectedId(q.id); qc.invalidateQueries({ queryKey: ["course", course.id, "exam-admin"] }); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  // Reorder
  useEffect(() => {
    if (!listRef.current || !exam) return;
    const s = Sortable.create(listRef.current, {
      animation: 150,
      handle: ".drag-handle",
      onEnd: async () => {
        const ids = Array.from(listRef.current!.querySelectorAll<HTMLElement>("[data-question]")).map((el) => el.dataset.question!);
        try {
          await request(`/exams/${exam.id}/questions/reorder`, { method: "POST", body: { ordered_ids: ids } });
          qc.invalidateQueries({ queryKey: ["course", course.id, "exam-admin"] });
          toast("Ordinea întrebărilor salvată", "success");
        } catch (err) { toast(err instanceof Error ? err.message : "Eroare", "error"); }
      },
    });
    return () => s.destroy();
  }, [exam, course.id, qc, toast]);

  if (!exam) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 48 }}>
        <i className="fa-solid fa-clipboard-check" style={{ fontSize: "2.4rem", color: "var(--c-primary)", marginBottom: 14 }}></i>
        <h3>Acest curs nu are încă un examen</h3>
        <p>Adaugă un examen pentru a permite cursanților să-l susțină după finalizarea cursului.</p>
        <button className="btn btn-primary btn-lg" onClick={() => createExam.mutate()}>
          <i className="fa-solid fa-plus"></i> Creează examen
        </button>
      </div>
    );
  }

  const selected = exam.questions.find((q) => q.id === selectedId) || null;

  return (
    <>
      <div className="grid-2" style={{ marginBottom: 24, alignItems: "start" }}>
        <ExamSettings exam={exam} courseId={course.id} />
        <ExamBehavior exam={exam} onUpdate={(k, v) => updateSetting.mutate({ [k]: v })} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 24 }}>
        <div>
          <div className="card card-pad-sm" style={{ position: "sticky", top: 90 }}>
            <div className="card-head"><h3 style={{ margin: 0, fontSize: "1rem" }}>Întrebări ({exam.questions.length})</h3><button className="btn btn-primary btn-sm" onClick={() => addQuestion.mutate()}><i className="fa-solid fa-plus"></i></button></div>
            <div ref={listRef}>
              {exam.questions.map((q, i) => {
                const isActive = q.id === selectedId;
                return (
                  <div
                    key={q.id}
                    data-question={q.id}
                    className="lesson-row"
                    style={isActive ? { borderColor: "var(--c-primary)", background: "var(--c-primary-light)" } : undefined}
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest(".drag-handle")) return;
                      setSelectedId(q.id);
                    }}
                  >
                    <span className="drag-handle"><i className="fa-solid fa-grip-vertical"></i></span>
                    <div className="type-ico type-mixed" style={isActive ? { background: "var(--c-primary)", color: "#fff" } : undefined}>{i + 1}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: ".85rem", display: "block" }}>{q.text.substring(0, 60)}{q.text.length > 60 ? "…" : ""}</strong>
                      <small className="muted">{q.points} pct</small>
                    </div>
                  </div>
                );
              })}
              {exam.questions.length === 0 && <div className="text-muted" style={{ padding: 12, textAlign: "center" }}>Niciun item.</div>}
            </div>
          </div>
        </div>
        <div>{selected ? <QuestionEditor question={selected} exam={exam} courseId={course.id} /> : <div className="card text-muted">Selectează o întrebare.</div>}</div>
      </div>
    </>
  );
}

function ExamSettings({ exam, courseId }: { exam: ExamOut; courseId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [title, setTitle] = useState(exam.title);
  const [time, setTime] = useState(exam.time_limit_minutes);
  const [pass, setPass] = useState(exam.passing_score);
  const [attempts, setAttempts] = useState(exam.max_attempts);

  useEffect(() => { setTitle(exam.title); setTime(exam.time_limit_minutes); setPass(exam.passing_score); setAttempts(exam.max_attempts); }, [exam.id, exam.title, exam.time_limit_minutes, exam.passing_score, exam.max_attempts]);

  const save = useMutation({
    mutationFn: () => request<ExamOut>(`/exams/${exam.id}`, { method: "PATCH", body: { title, time_limit_minutes: time, passing_score: pass, max_attempts: attempts } }),
    onSuccess: () => { toast("Setări examen salvate", "success"); qc.invalidateQueries({ queryKey: ["course", courseId, "exam-admin"] }); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  return (
    <div className="card">
      <h3>Configurare examen</h3>
      <div className="form-group"><label>Titlu examen</label><input className="form-control" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
      <div className="grid-2">
        <div className="form-group"><label>Timp (minute)</label><input className="form-control" type="number" value={time} onChange={(e) => setTime(Number(e.target.value))} /></div>
        <div className="form-group"><label>Notă promovare</label><input className="form-control" type="number" step="0.1" value={pass} onChange={(e) => setPass(Number(e.target.value))} /></div>
      </div>
      <div className="grid-2">
        <div className="form-group"><label>Încercări maxime</label><input className="form-control" type="number" value={attempts} onChange={(e) => setAttempts(Number(e.target.value))} /></div>
        <div className="form-group">&nbsp;<button className="btn btn-primary btn-block" onClick={() => save.mutate()} disabled={save.isPending}><i className="fa-solid fa-floppy-disk"></i> Salvează</button></div>
      </div>
    </div>
  );
}

function ExamBehavior({ exam, onUpdate }: { exam: ExamOut; onUpdate: (key: string, value: boolean) => void }) {
  const toggles: { key: keyof ExamOut; label: string }[] = [
    { key: "shuffle_questions", label: "Amestecă ordinea întrebărilor" },
    { key: "shuffle_answers", label: "Amestecă variantele de răspuns" },
    { key: "show_correct_after", label: "Afișează răspunsurile corecte după examen" },
    { key: "allow_review", label: "Permite revenirea la întrebări" },
    { key: "issue_certificate", label: "Eliberează certificat la promovare" },
  ];
  return (
    <div className="card">
      <h3>Comportament</h3>
      {toggles.map((t) => (
        <div key={t.key as string} className="toggle-row" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, padding: "14px 0", borderBottom: "1px solid var(--c-line-2)" }}>
          <div className="info"><strong>{t.label}</strong></div>
          <label className="switch" style={{ position: "relative", display: "inline-block", width: 42, height: 24, flexShrink: 0 }}>
            <input
              type="checkbox"
              checked={!!exam[t.key]}
              onChange={(e) => onUpdate(t.key as string, e.target.checked)}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{ position: "absolute", cursor: "pointer", inset: 0, background: exam[t.key] ? "var(--c-primary)" : "var(--c-line)", borderRadius: 24, transition: ".2s" }}>
              <span style={{ position: "absolute", content: '""', height: 18, width: 18, left: exam[t.key] ? 21 : 3, top: 3, background: "#fff", borderRadius: "50%", transition: ".2s", boxShadow: "0 1px 3px rgba(0,0,0,.2)" }}></span>
            </span>
          </label>
        </div>
      ))}
    </div>
  );
}

interface AnswerDraft { text: string; is_correct: boolean; }

function QuestionEditor({ question, exam, courseId }: { question: ExamQuestion; exam: ExamOut; courseId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [text, setText] = useState(question.text);
  const [type, setType] = useState<QuestionType>(question.type);
  const [points, setPoints] = useState(question.points);
  const [expl, setExpl] = useState(question.explanation || "");
  const [answers, setAnswers] = useState<AnswerDraft[]>(question.answers.map((a) => ({ text: a.text, is_correct: a.is_correct || false })));

  useEffect(() => {
    setText(question.text);
    setType(question.type);
    setPoints(question.points);
    setExpl(question.explanation || "");
    setAnswers(question.answers.map((a) => ({ text: a.text, is_correct: a.is_correct || false })));
  }, [question.id]);

  const save = useMutation({
    mutationFn: () => request(`/exam-questions/${question.id}`, {
      method: "PATCH",
      body: { text, type, points, explanation: expl || null, answers },
    }),
    onSuccess: () => { toast("Întrebare salvată", "success"); qc.invalidateQueries({ queryKey: ["course", courseId, "exam-admin"] }); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });
  const del = useMutation({
    mutationFn: () => request(`/exam-questions/${question.id}`, { method: "DELETE" }),
    onSuccess: () => { toast("Întrebare ștearsă", "success"); qc.invalidateQueries({ queryKey: ["course", courseId, "exam-admin"] }); },
    onError: (err) => toast(err instanceof Error ? err.message : "Eroare", "error"),
  });

  function toggleCorrect(i: number) {
    const cur = answers[i];
    let next = answers.map((a) => ({ ...a }));
    if (type === "single" || type === "true_false") {
      next = next.map((a, j) => ({ ...a, is_correct: j === i ? !cur.is_correct : false }));
    } else {
      next[i].is_correct = !cur.is_correct;
    }
    setAnswers(next);
  }

  return (
    <div className="card">
      <div className="card-head">
        <div><small className="muted">Editează întrebarea</small><h3 style={{ margin: "2px 0 0" }}>Întrebare grilă</h3></div>
        <button className="btn btn-accent btn-sm" onClick={() => { if (confirm("Ștergi această întrebare?")) del.mutate(); }}>
          <i className="fa-solid fa-trash"></i>
        </button>
      </div>
      <div className="form-group"><label>Enunț</label><textarea className="form-control" style={{ minHeight: 90 }} value={text} onChange={(e) => setText(e.target.value)} /></div>
      <div className="grid-2">
        <div className="form-group">
          <label>Tip</label>
          <select className="form-control" value={type} onChange={(e) => setType(e.target.value as QuestionType)}>
            <option value="single">Răspuns unic</option>
            <option value="multiple">Răspunsuri multiple</option>
            <option value="true_false">Adevărat / Fals</option>
          </select>
        </div>
        <div className="form-group"><label>Punctaj</label><input className="form-control" type="number" step="0.1" value={points} onChange={(e) => setPoints(Number(e.target.value))} /></div>
      </div>
      <div className="form-group">
        <label>Variante de răspuns <small className="muted" style={{ fontWeight: 500 }}>(bifează cele corecte)</small></label>
        {answers.map((a, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: 8, marginBottom: 6, border: "1px solid var(--c-line)", borderRadius: 6, background: a.is_correct ? "#f3faf6" : "#fff", borderColor: a.is_correct ? "var(--c-success)" : undefined }}>
            <div style={{ width: 26, height: 26, borderRadius: "50%", background: a.is_correct ? "var(--c-success)" : "var(--c-bg)", color: a.is_correct ? "#fff" : "var(--c-muted)", display: "grid", placeItems: "center", fontWeight: 700, fontSize: ".8rem", border: "1px solid var(--c-line)", flexShrink: 0 }}>
              {String.fromCharCode(65 + i)}
            </div>
            <input className="txt" style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontFamily: "inherit", fontSize: ".9rem", padding: 4 }}
              value={a.text}
              onChange={(e) => setAnswers(answers.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} />
            <label className="form-check"><input type="checkbox" checked={a.is_correct} onChange={() => toggleCorrect(i)} /> Corect</label>
            <button className="btn btn-ghost btn-sm" onClick={() => setAnswers(answers.filter((_, j) => j !== i))}><i className="fa-solid fa-trash"></i></button>
          </div>
        ))}
        <button className="btn btn-ghost btn-sm mt-16" onClick={() => setAnswers([...answers, { text: "Variantă nouă", is_correct: false }])}>
          <i className="fa-solid fa-plus"></i> Adaugă variantă
        </button>
      </div>
      <div className="form-group"><label>Explicație (afișată după examen)</label><textarea className="form-control" style={{ minHeight: 80 }} value={expl} onChange={(e) => setExpl(e.target.value)} /></div>
      <div className="form-row" style={{ borderTop: "1px solid var(--c-line)", paddingTop: 18 }}>
        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={save.isPending}>
          <i className="fa-solid fa-floppy-disk"></i> {save.isPending ? "Se salvează..." : "Salvează întrebarea"}
        </button>
      </div>
    </div>
  );
}
